import {
  createRouter,
  createRoute,
  createRootRoute,
  redirect,
  notFound,
} from '@tanstack/react-router';
import Cookies from 'js-cookie';

import { APIfetchAssets } from './api/Assets';
import {
  APIlogout,
  APIemailConfirmation,
  APIfetchProfile,
  loadUserProfile,
} from './api/Auth';
import {
  APIfetchMemories,
  APIfetchSprks,
  APIfetchMemory,
  APIfetchMemoryComments,
  APIfetchMemoryShares,
  APIfetchReceivedMemories,
  APIfetchReceivedSprks,
} from './api/Memory';
import { APIgetOverallStats } from './api/Stats';
import {
  APIfetchContributorInvitations,
  APIfetchTimeline,
  APIfetchTimelines,
} from './api/Timeline';

import App from './App';
import Login from './containers/Login';
import SignUp from './containers/SignUp';
import EmailConfirmation from './containers/EmailConfirmation';
import PasswordReset from './containers/PasswordReset';
import PasswordChange from './containers/PasswordChange';
import Account from './containers/Account';
import AccountProfile from './components/Account/Profile';
import AccountPassword from './components/Account/Password';
import Dashboard from './containers/Dashboard';
import DashboardHome from './components/Dashboard/Home';
import DashboardSprks from './components/Dashboard/Sprks';
import DashboardMemories from './components/Dashboard/Memories';
import DashboardTimelines from './components/Dashboard/Timelines';
import DashboardInvitations from './components/Dashboard/Invitations';
import DashboardMemory from './components/Dashboard/Memory';
import MemoryEdit from './containers/MemoryEdit';
import MemoryView from './containers/MemoryView';
import MemoryViewPublic from './containers/MemoryViewPublic';
import TimelineCreate from './containers/TimelineCreate';
import TimelineView from './containers/TimelineView';
import TimelineContributorAccept from './containers/TimelineContributorAccept';

import Loading from './components/Loading';
import NotFound from './components/NotFound';

import { getCurrentPage } from './helpers';

import { useStore } from './store';

import type { User } from './types/user';

const rootRoute = createRootRoute({
  component: App,
  head: () => ({
    meta: [
      {
        title: 'LGCY Story',
      },
    ],
  }),
});

const isUserAuthenticated = async () => {
  let { profile } = useStore.getState() as { profile: User | null };
  if (!(profile && profile.id)) {
    profile = await loadUserProfile();
  }
  return profile && profile.id;
};

const checkAuthentication = async () => {
  const authenticated = await isUserAuthenticated();
  if (!authenticated) {
    throw redirect({
      to: '/login',
      search: {
        redirect: location.href,
      },
    });
  }
};

export const loginRoute = createRoute({
  component: Login,
  getParentRoute: () => rootRoute,
  path: 'login',
});

export const logoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'logout',
  beforeLoad: async () => {
    await APIlogout();
    Cookies.remove('laravel-session');
    useStore.setState({ profile: null });
    throw redirect({ to: '/' });
  },
});

export const signUpRoute = createRoute({
  component: SignUp,
  getParentRoute: () => rootRoute,
  path: 'signup',
});

export const emailConfirmationRoute = createRoute({
  component: EmailConfirmation,
  getParentRoute: () => rootRoute,
  path: 'signup/confirm/$token',
  loader: {
    handler: async ({ params }) => {
      const confirmationResult = APIemailConfirmation(params.token);
      return { confirmationResult };
    },
  },
  gcTime: 10,
  pendingMinMs: 100,
  pendingMs: 100,
});

export const passwordResetRoute = createRoute({
  component: PasswordReset,
  getParentRoute: () => rootRoute,
  path: 'password/reset',
});

export const passwordChangeRoute = createRoute({
  component: PasswordChange,
  getParentRoute: () => rootRoute,
  path: 'password/change/$token',
});

export const homeRoute = createRoute({
  component: () => ('<div>Home</div>'),
  getParentRoute: () => rootRoute,
  beforeLoad: () => {
    throw redirect({ to: '/dashboard' });
  },
  path: '/',
});

export const memoriesRoute = createRoute({
  component: () => ('<div>Memories?</div>'),
  getParentRoute: () => rootRoute,
  path: '/memories',
  beforeLoad: checkAuthentication,
});

export const memoriesIndexRoute = createRoute({
  component: () => ('<div>Memories index</div>'),
  getParentRoute: () => memoriesRoute,
  path: '/',
  beforeLoad: checkAuthentication,
});

export const memoryCreateRoute = createRoute({
  component: MemoryEdit,
  getParentRoute: () => rootRoute,
  path: 'memories/create',
  beforeLoad: checkAuthentication,
  loader: {
    handler: async () => {
      const sharedAssets = await APIfetchAssets({ shared: true });
      return {
        sharedAssets,
      };
    },
  },
  remountDeps(opt) {
    return {
      ...opt,
      key: Math.random().toString(), // Force remount on every visit to this route
    }
  },
});

export const memoryEditRoute = createRoute({
  component: MemoryEdit,
  getParentRoute: () => rootRoute,
  path: 'memories/$memoryId/edit',
  beforeLoad: isUserAuthenticated,
  loader: {
    handler: async ({ params }) => {
      const memoryId = params.memoryId;
      const memory = await APIfetchMemory(memoryId);
      if (!memoryId || !memory) {
        throw redirect({ to: '/memories/create' });
      }
      if (!memory.capabilities.can_update) {
        throw redirect({ to: new URL(memory.url).pathname });
      }
      const sharedAssets = await APIfetchAssets({ shared: true });
      return {
        memory,
        sharedAssets,
      };
    },
  },
  gcTime: 10,
  pendingMinMs: 100,
  pendingMs: 100,
});

export const memoryViewRoute = createRoute({
  component: MemoryView,
  getParentRoute: () => rootRoute,
  path: 'memories/$memoryId',
  beforeLoad: isUserAuthenticated,
  loader: {
    handler: async ({ params }) => {
      const memoryId = params.memoryId;
      const memory = await APIfetchMemory(memoryId);
      if (!memoryId || !memory) {
        throw redirect({ to: '/' });
      }
      if (!memory.capabilities.can_update) {
        throw redirect({ to: new URL(memory.url).pathname });
      }
      const commentsPromise = APIfetchMemoryComments(memoryId);
      return {
        memory,
        comments: commentsPromise,
      };
    },
  },
  gcTime: 10,
  pendingMinMs: 100,
  pendingMs: 100,
});

export const memoryViewPublicRoute = createRoute({
  component: MemoryViewPublic,
  getParentRoute: () => rootRoute,
  path: 'm/$memoryId',
  loader: {
    handler: async ({ params }) => {
      const memoryId = params.memoryId;
      const memory = await APIfetchMemory(memoryId);
      if (!memoryId || !memory) {
        throw redirect({ to: '/' });
      }
      const commentsPromise = APIfetchMemoryComments(memoryId);
      return {
        memory,
        comments: commentsPromise,
      };
    },
  },
  gcTime: 10,
  pendingMinMs: 100,
  pendingMs: 100,
});

export const timelineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'timelines',
});

export const timelineCreateRoute = createRoute({
  component: TimelineCreate,
  getParentRoute: () => timelineRoute,
  path: 'create',
  beforeLoad: checkAuthentication,
  loader: {
    handler: async () => {
      return {};
    },
  },
  gcTime: 10,
  pendingMinMs: 100,
  pendingMs: 100,
});

export const timelineContributorAcceptRoute = createRoute({
  component: TimelineContributorAccept,
  getParentRoute: () => timelineRoute,
  path: '$timelineId/contributors/$contributorId',
  beforeLoad: checkAuthentication,
  loader: {
    handler: async ({ params }) => {
      const timelineId = params.timelineId;
      const contributorId = params.contributorId;
      try {
        const timeline = await APIfetchTimeline(timelineId);
        return { timeline, contributorId };
      } catch (error) {
        console.log(error);
        throw notFound();
      }
    },
  },
  notFoundComponent: NotFound,
  gcTime: 10,
  pendingMinMs: 100,
  pendingMs: 100,
});

export const timelineEditRoute = createRoute({
  component: TimelineCreate,
  getParentRoute: () => timelineRoute,
  path: '$timelineId/edit',
  beforeLoad: checkAuthentication,
  loader: {
    handler: async ({ params }) => {
      const timelineId = params.timelineId;
      try {
        const timeline = await APIfetchTimeline(timelineId);
        if (!timeline.capabilities.can_update && !timeline.capabilities.can_contribute) {
          throw redirect({ to: new URL(timeline.url).pathname });
        }
        return { timeline };
      } catch (error) {
        console.log(error);
        throw notFound();
      }
    },
  },
  notFoundComponent: NotFound,
  gcTime: 10,
  pendingMinMs: 100,
  pendingMs: 100,
});

export const timelineViewRoute = createRoute({
  component: TimelineView,
  getParentRoute: () => timelineRoute,
  path: '$timelineId',
  beforeLoad: isUserAuthenticated,
  loader: {
    handler: async ({ params }) => {
      const timelineId = params.timelineId;
      const timeline = await APIfetchTimeline(timelineId);
      if (timeline.status === 404) {
        throw notFound();
      }
      if (!timeline.capabilities.can_update && !timeline.capabilities.can_contribute) {
        throw redirect({ to: new URL(timeline.url).pathname });
      }
      return {
        timeline,
        mode: 'private',
      };
    },
  },
  notFoundComponent: NotFound,
  gcTime: 10,
  pendingMinMs: 100,
  pendingMs: 100,
});

export const timelineViewPublicRoute = createRoute({
  component: TimelineView,
  getParentRoute: () => rootRoute,
  path: 't/$timelineId',
  loader: {
    handler: async ({ params }) => {
      const timelineId = params.timelineId;
      const timeline = await APIfetchTimeline(timelineId);
      if (timeline.status === 404) {
        throw notFound();
      }
      return {
        timeline,
        mode: 'public',
      };
    },
  },
  notFoundComponent: NotFound,
  gcTime: 10,
  pendingMinMs: 100,
  pendingMs: 100,
});

export const timelineMemoryViewRoute = createRoute({
  component: MemoryView,
  getParentRoute: () => timelineRoute,
  path: '$timelineId/$memoryId',
  beforeLoad: isUserAuthenticated,
  loader: {
    handler: async ({ params }) => {
      const timelineId = params.timelineId;
      const memoryId = params.memoryId;
      const timeline = await APIfetchTimeline(timelineId);
      if (timeline.status === 404) {
        throw notFound();
      }
      const memory = await APIfetchMemory(memoryId, { timeline_id: timelineId });
      const commentsPromise = APIfetchMemoryComments(memoryId);
      return {
        timeline,
        memory,
        comments: commentsPromise,
      };
    },
  },
  notFoundComponent: NotFound,
  gcTime: 10,
  pendingMinMs: 100,
  pendingMs: 100,
});

export const timelineMemoryViewPublicRoute = createRoute({
  component: MemoryViewPublic,
  getParentRoute: () => rootRoute,
  path: '/t/$timelineId/$memoryId',
  loader: {
    handler: async ({ params }) => {
      const timelineId = params.timelineId;
      const memoryId = params.memoryId;
        const timeline = await APIfetchTimeline(timelineId);
        if (timeline.status === 404) {
          throw notFound();
        }
        const memory = await APIfetchMemory(memoryId, { timeline_id: timelineId });
        const commentsPromise = APIfetchMemoryComments(memoryId);
        return {
          timeline,
          memory,
          comments: commentsPromise,
      };
    },
  },
  notFoundComponent: NotFound,
  gcTime: 10,
  pendingMinMs: 100,
  pendingMs: 100,
});

export const timelineMemoryCreateRoute = createRoute({
  component: MemoryEdit,
  getParentRoute: () => timelineRoute,
  path: '$timelineId/create',
  beforeLoad: isUserAuthenticated,
  loader: {
    handler: async ({ params }) => {
      const timelineId = params.timelineId;
      const timeline = await APIfetchTimeline(timelineId);
      return {
        timeline,
        sharedAssets: [],
      };
    },
  },
  gcTime: 10,
  pendingMinMs: 100,
  pendingMs: 100,
});

export const timelineMemoryEditRoute = createRoute({
  component: MemoryEdit,
  getParentRoute: () => timelineRoute,
  path: '$timelineId/$memoryId/edit',
  beforeLoad: isUserAuthenticated,
  loader: {
    handler: async ({ params }) => {
      const timelineId = params.timelineId;
      const memoryId = params.memoryId;
      const memory = await APIfetchMemory(memoryId, { timeline_id: timelineId });
      if (!memoryId || !memory) {
        throw notFound();
      }
      if (!memory.capabilities.can_update) {
        throw redirect({ to: new URL(memory.url).pathname });
      }
      const timeline = await APIfetchTimeline(timelineId);
      return {
        memory,
        timeline,
        sharedAssets: [],
      };
    },
  },
  gcTime: 10,
  pendingMinMs: 100,
  pendingMs: 100,
});

export const dashboardRoute = createRoute({
  component: Dashboard,
  getParentRoute: () => rootRoute,
  path: 'dashboard',
  beforeLoad: checkAuthentication,
  loader: {
    handler: async () => {
      const stats = APIgetOverallStats();
      return { stats };
    },
  },
  gcTime: 10,
  pendingMinMs: 100,
  pendingMs: 100,
});

export const dashboardIndexRoute = createRoute({
  component: DashboardHome,
  getParentRoute: () => dashboardRoute,
  path: '/',
  beforeLoad: checkAuthentication,
  loader: {
    handler: async () => {
      const memories = await APIfetchMemories({ per_page: 3 });
      const timelines = await APIfetchTimelines({ per_page: 3 });
      const sprks = await APIfetchSprks({ per_page: 4 });
      const stats = APIgetOverallStats();
      return {
        memories,
        timelines,
        sprks,
        stats,
      };
    },
  },
  gcTime: 10,
  pendingMinMs: 100,
  pendingMs: 100,
});

export const dashboardSprksRoute = createRoute({
  component: DashboardSprks,
  getParentRoute: () => dashboardRoute,
  path: 'sprks',
  beforeLoad: checkAuthentication,
  loader: {
    handler: async () => {
      const page = getCurrentPage();
      const sprks = await APIfetchSprks({ page: page });
      return { sprks };
    },
  },
  pendingComponent: Loading,
  gcTime: 10,
  pendingMinMs: 100,
  pendingMs: 100,
});

export const dashboardSprkRoute = createRoute({
  component: DashboardMemory,
  getParentRoute: () => dashboardRoute,
  path: 'sprks/$sprkId',
  beforeLoad: checkAuthentication,
  loader: {
    handler: async ({ params }) => {
      const sprkId = params.sprkId;
      const memory = await APIfetchMemory(sprkId);
      const shares = APIfetchMemoryShares(sprkId);
      return {
        memory,
        shares,
      };
    },
  },
  pendingComponent: Loading,
  gcTime: 10,
  pendingMinMs: 100,
  pendingMs: 100,
});

export const dashboardMemoriesRoute = createRoute({
  component: DashboardMemories,
  getParentRoute: () => dashboardRoute,
  path: 'memories',
  beforeLoad: checkAuthentication,
  loader: {
    handler: async () => {
      const page = getCurrentPage();
      const memories = await APIfetchMemories({ page: page });
      return { memories };
    },
  },
  pendingComponent: Loading,
  gcTime: 10,
  pendingMinMs: 100,
  pendingMs: 100,
});

export const dashboardMemoryRoute = createRoute({
  component: DashboardMemory,
  getParentRoute: () => dashboardRoute,
  path: 'memories/$memoryId',
  beforeLoad: checkAuthentication,
  loader: {
    handler: async ({ params }) => {
      const memoryId = params.memoryId;
      const memory = await APIfetchMemory(memoryId);
      const shares = APIfetchMemoryShares(memoryId);
      return {
        memory,
        shares,
      };
    },
  },
  pendingComponent: Loading,
  gcTime: 10,
  pendingMinMs: 100,
  pendingMs: 100,
});

export const dashboardTimelinesRoute = createRoute({
  component: DashboardTimelines,
  getParentRoute: () => dashboardRoute,
  path: 'timelines',
  beforeLoad: checkAuthentication,
  loader: {
    handler: async () => {
      const page = getCurrentPage();
      const timelines = await APIfetchTimelines({ page: page });
      return { timelines };
    },
  },
  pendingComponent: Loading,
  gcTime: 10,
  pendingMinMs: 100,
  pendingMs: 100,
});

export const dashboardContributionsRoute = createRoute({
  component: DashboardTimelines,
  getParentRoute: () => dashboardRoute,
  path: 'contributing',
  beforeLoad: checkAuthentication,
  loader: {
    handler: async () => {
      const page = getCurrentPage();
      const timelines = await APIfetchTimelines({ contributing: 1, page: page });
      return {
        timelines,
        mode: 'contributing',
      };
    },
  },
  pendingComponent: Loading,
  gcTime: 10,
  pendingMinMs: 100,
  pendingMs: 100,
});

export const dashboardReceivedSprksRoute = createRoute({
  component: DashboardSprks,
  getParentRoute: () => dashboardRoute,
  path: 'received/sprks',
  beforeLoad: checkAuthentication,
  loader: {
    handler: async () => {
      const page = getCurrentPage();
      const sprks = await APIfetchReceivedSprks({ page: page });
      return { sprks };
    },
  },
  pendingComponent: Loading,
  gcTime: 10,
  pendingMinMs: 100,
  pendingMs: 100,
});

export const dashboardReceivedMemoriesRoute = createRoute({
  component: DashboardMemories,
  getParentRoute: () => dashboardRoute,
  path: 'received/memories',
  beforeLoad: checkAuthentication,
  loader: {
    handler: async () => {
      const page = getCurrentPage();
      const memories = await APIfetchReceivedMemories({ page: page });
      return { memories };
    },
  },
  pendingComponent: Loading,
  gcTime: 10,
  pendingMinMs: 100,
  pendingMs: 100,
});

export const dashboardInvitationsRoute = createRoute({
  component: DashboardInvitations,
  getParentRoute: () => dashboardRoute,
  path: 'invitations',
  beforeLoad: checkAuthentication,
  loader: {
    handler: async () => {
      const invitations = await APIfetchContributorInvitations();
      return { invitations };
    },
  },
  pendingComponent: Loading,
  gcTime: 10,
  pendingMinMs: 100,
  pendingMs: 100,
});

export const accountRoute = createRoute({
  component: Account,
  getParentRoute: () => rootRoute,
  path: 'account',
  beforeLoad: checkAuthentication,
  loader: {
    handler: async () => {
      const profile = await APIfetchProfile();
      return { profile: profile.data };
    },
  },
  gcTime: 10,
  pendingMinMs: 100,
  pendingMs: 100,
});

export const accountProfileRoute = createRoute({
  component: AccountProfile,
  getParentRoute: () => accountRoute,
  path: '/',
});

export const accountPasswordRoute = createRoute({
  component: AccountPassword,
  getParentRoute: () => accountRoute,
  path: 'password',
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  logoutRoute,
  signUpRoute,
  emailConfirmationRoute,
  passwordResetRoute,
  passwordChangeRoute,
  homeRoute,
  memoriesRoute,
  memoriesIndexRoute,
  memoryCreateRoute,
  memoryEditRoute,
  memoryViewRoute,
  memoryViewPublicRoute,
  timelineRoute,
  timelineCreateRoute,
  timelineViewRoute,
  timelineEditRoute,
  timelineViewPublicRoute,
  timelineMemoryViewRoute,
  timelineMemoryViewPublicRoute,
  timelineMemoryCreateRoute,
  timelineMemoryEditRoute,
  timelineContributorAcceptRoute,
  dashboardRoute,
  dashboardIndexRoute,
  dashboardSprksRoute,
  dashboardSprkRoute,
  dashboardMemoriesRoute,
  dashboardMemoryRoute,
  dashboardTimelinesRoute,
  dashboardReceivedSprksRoute,
  dashboardReceivedMemoriesRoute,
  dashboardContributionsRoute,
  dashboardInvitationsRoute,
  accountRoute,
  accountProfileRoute,
  accountPasswordRoute,
]);

const router = createRouter({
  routeTree,
  defaultPendingComponent: Loading,
});

export default router;
